import { defineStore } from 'pinia';
import { getUserId } from '@/utils/auth-token';
import { normalizeAnalyticsApiKey } from '@/utils/analytics-path';
import {
  ANALYTICS_LOG_SCHEMA_VERSION,
  buildAnalyticsLogRow,
  createAnalyticsId,
  normalizeAnalyticsLogRow,
} from '@/utils/analytics-log';
import { resolveAnalyticsClientSurface } from '@/utils/analytics-device';
import {
  idbGetAllLogs,
  idbGetAllMeta,
  idbSyncHotLogs,
  idbSetMetaMap,
} from '@/utils/analytics-idb';
import {
  ANALYTICS_HOT_RETENTION_MS,
  filterDurableHotLogs,
  filterHotAnalyticsLogs,
  listAnalyticsArchiveSummaries,
  runAnalyticsDayArchive,
} from '@/utils/analytics-retention';
import { trackLaEventByKey } from '@/utils/la-track';

const LEGACY_STORAGE_KEYS = ['vantag-analytics-v3', 'vantag-analytics-v2'];
const PERSIST_DEBOUNCE_MS = 2000;

/**
 * Outbox 去重键：同 order/item 的充值中间态不互相覆盖错误事件。
 * @param {{ key?: string, props?: unknown }} row
 * @returns {string|undefined}
 */
function resolveLaOutboxDedupeKey(row) {
  const key = String(row?.key || '').trim();
  if (!key) return undefined;
  const props =
    row?.props && typeof row.props === 'object' && !Array.isArray(row.props)
      ? row.props
      : null;
  if (!props) return undefined;

  const orderId = String(props.orderId ?? props.coboOrderId ?? '').trim();
  if (orderId && (key.startsWith('deposit.') || key.startsWith('trade.'))) {
    return `${key}:${orderId}`;
  }

  const itemId = String(props.itemId ?? '').trim();
  if (itemId && key.startsWith('deposit.')) return `${key}:${itemId}`;

  const txHash = String(props.txHash ?? '').trim();
  if (txHash && (key === 'deposit.transfer_submitted' || key === 'deposit.chain_confirmed')) {
    return `${key}:${txHash}`;
  }

  return undefined;
}

function createDeviceId() {
  return createAnalyticsId();
}

function readLegacyLocalStorage() {
  if (typeof localStorage === 'undefined') return null;
  for (const key of LEGACY_STORAGE_KEYS) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') return parsed;
    } catch {
      /* ignore */
    }
  }
  return null;
}

function clearLegacyLocalStorage() {
  if (typeof localStorage === 'undefined') return;
  LEGACY_STORAGE_KEYS.forEach((key) => {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  });
}

let persistTimer = null;
/** @type {Promise<void> | null} */
let hydratePromise = null;
/** @type {Array<Record<string, unknown>>} */
let pendingTrackEntries = [];

/**
 * 标准 log 行：
 * {
 *   v, id, at, key, label,
 *   deviceId?, userId?, routeName?,
 *   platform?, ua?, viewportW?,
 *   funnelSessionId?, durationMs?, props,
 * }
 * 存储：IndexedDB（近 3 天热日志 + 日 zip 归档）；ui.click 仅内存。
 */
export const useAnalyticsStore = defineStore('analytics', {
  state: () => ({
    hydrated: false,
    deviceId: '',
    userId: '',
    routeName: '',
    routeEnteredAt: 0,
    pageVisible: true,
    /** @type {Record<string, number>} */
    pageDwellMs: {},
    /**
     * @type {Record<string, {
     *   key: string,
     *   method: string,
     *   hits: number,
     *   lastAt: number,
     *   lastOk: boolean,
     *   lastDurationMs: number,
     *   lastCode?: string|number|null,
     * }>}
     */
    apiActivity: {},
    /**
     * @type {Array<Record<string, unknown>>}
     */
    logs: [],
    /**
     * @type {null | {
     *   id: string,
     *   routeName: string,
     *   enteredAt: number,
     *   readyAt: number|null,
     *   engageStartedAt: number|null,
     *   cold: boolean|null,
     *   apiMs: number,
     *   apiCount: number,
     *   jsMs: number,
     *   dataMs: number,
     *   apis: Array<{ key: string, ms: number, ok: boolean }>,
     * }}
     */
    pageVisit: null,
    /** ready 之后的真留存累计（按路由） */
    pageEngageMs: {},
    /** 付款尝试会话 */
    funnelSessionId: '',
    funnelStartedAt: 0,
    /** @type {Record<string, { id: string, startedAt: number }>} 按业务域隔离的漏斗会话 */
    funnelSessions: {},
    /** @type {Record<string, number>} */
    flowStartedAt: {},
    /** @type {Array<{ day: string, byteLength: number, logCount: number, createdAt: number }>} */
    archiveSummaries: [],
  }),

  getters: {
    /** @deprecated 兼容旧读法，等同 logs */
    actions(state) {
      return state.logs;
    },
    snapshot(state) {
      const surface = resolveAnalyticsClientSurface();
      return {
        schemaVersion: ANALYTICS_LOG_SCHEMA_VERSION,
        storage: 'indexeddb',
        hotRetentionDays: 3,
        deviceId: state.deviceId,
        userId: state.userId,
        routeName: state.routeName,
        platform: surface.platform,
        ua: surface.ua,
        viewportW: surface.viewportW,
        pageDwellMs: { ...state.pageDwellMs },
        pageEngageMs: { ...state.pageEngageMs },
        funnelSessionId: state.funnelSessionId || undefined,
        funnelSessions: { ...state.funnelSessions },
        pageVisit: state.pageVisit
          ? {
              routeName: state.pageVisit.routeName,
              cold: state.pageVisit.cold,
              ready: Boolean(state.pageVisit.readyAt),
              apiMs: state.pageVisit.apiMs,
              dataMs: state.pageVisit.dataMs,
              jsMs: state.pageVisit.jsMs,
            }
          : null,
        apiActivity: { ...state.apiActivity },
        archives: state.archiveSummaries.slice(),
        logs: state.logs.slice(),
      };
    },
  },

  actions: {
    /**
     * 异步从 IndexedDB hydrate（兼容一次性迁入旧 localStorage）。
     * @returns {Promise<void>}
     */
    hydrate() {
      if (this.hydrated) return Promise.resolve();
      if (hydratePromise) return hydratePromise;
      hydratePromise = this._hydrateImpl().finally(() => {
        hydratePromise = null;
      });
      return hydratePromise;
    },

    async _hydrateImpl() {
      let meta = {};
      let idbLogs = [];
      try {
        meta = await idbGetAllMeta();
        idbLogs = await idbGetAllLogs();
      } catch (err) {
        console.warn('[analytics] idb hydrate failed', err);
      }

      const legacy = !meta.deviceId && !idbLogs.length ? readLegacyLocalStorage() : null;
      if (legacy) {
        meta = {
          deviceId: legacy.deviceId,
          userId: legacy.userId,
          pageDwellMs: legacy.pageDwellMs,
          pageEngageMs: legacy.pageEngageMs,
          apiActivity: legacy.apiActivity,
          funnelSessionId: legacy.funnelSessionId,
          funnelStartedAt: legacy.funnelStartedAt,
        };
        const list = Array.isArray(legacy.logs)
          ? legacy.logs
          : Array.isArray(legacy.actions)
            ? legacy.actions
            : [];
        idbLogs = list;
      }

      const deviceId =
        (typeof meta.deviceId === 'string' && meta.deviceId.trim()) || createDeviceId();
      this.deviceId = deviceId;

      if (meta.pageDwellMs && typeof meta.pageDwellMs === 'object') {
        this.pageDwellMs = { ...meta.pageDwellMs };
      }
      if (meta.pageEngageMs && typeof meta.pageEngageMs === 'object') {
        this.pageEngageMs = { ...meta.pageEngageMs };
      }
      if (meta.apiActivity && typeof meta.apiActivity === 'object') {
        this.apiActivity = { ...meta.apiActivity };
      }
      if (typeof meta.userId === 'string' && meta.userId) {
        this.userId = meta.userId;
      }
      if (typeof meta.funnelSessionId === 'string' && meta.funnelSessionId.trim()) {
        this.funnelSessionId = meta.funnelSessionId.trim();
        this.funnelStartedAt = Number(meta.funnelStartedAt) || Date.now();
      }
      if (meta.funnelSessions && typeof meta.funnelSessions === 'object') {
        const restored = {};
        Object.entries(meta.funnelSessions).forEach(([key, value]) => {
          const id = String(value?.id ?? '').trim();
          if (!key || !id) return;
          restored[key] = {
            id,
            startedAt: Number(value?.startedAt) || Date.now(),
          };
        });
        this.funnelSessions = { ...restored, ...this.funnelSessions };
      }

      const normalized = idbLogs.map((row) => normalizeAnalyticsLogRow(row)).filter(Boolean);
      this.logs = filterHotAnalyticsLogs(normalized);

      const liveUserId = getUserId() || '';
      if (liveUserId) this.userId = liveUserId;
      this.pageVisible = typeof document === 'undefined' ? true : !document.hidden;
      this.hydrated = true;

      if (pendingTrackEntries.length) {
        const queued = pendingTrackEntries.slice();
        pendingTrackEntries = [];
        queued.forEach((entry) => this.trackLog(entry));
      }

      await this.persistNow();
      if (legacy) clearLegacyLocalStorage();
      await this.runRetentionAndArchive();
    },

    async persistNow() {
      if (!this.hydrated) return;
      if (persistTimer) {
        clearTimeout(persistTimer);
        persistTimer = null;
      }
      this.logs = filterHotAnalyticsLogs(this.logs);
      // durable 可能仍是 Pinia Proxy；idb* 层会 cloneForIdb 后再 put
      const durable = filterDurableHotLogs(this.logs);
      const minAt = Date.now() - ANALYTICS_HOT_RETENTION_MS;
      try {
        await idbSetMetaMap({
          deviceId: this.deviceId,
          userId: this.userId,
          pageDwellMs: this.pageDwellMs,
          pageEngageMs: this.pageEngageMs,
          apiActivity: this.apiActivity,
          funnelSessionId: this.funnelSessionId || '',
          funnelStartedAt: this.funnelStartedAt || 0,
          funnelSessions: this.funnelSessions,
          savedAt: Date.now(),
        });
        await idbSyncHotLogs(durable, minAt);
      } catch (err) {
        console.warn('[analytics] idb persist failed', err);
      }
    },

    schedulePersist() {
      if (!this.hydrated) return;
      if (persistTimer) clearTimeout(persistTimer);
      persistTimer = setTimeout(() => {
        persistTimer = null;
        void this.persistNow();
      }, PERSIST_DEBOUNCE_MS);
    },

    /** 静默：过期日打 zip 归档 + 刷新归档摘要 */
    async runRetentionAndArchive() {
      if (!this.hydrated) return;
      try {
        await runAnalyticsDayArchive({
          deviceId: this.deviceId,
          userId: this.userId,
        });
        this.archiveSummaries = await listAnalyticsArchiveSummaries();
        this.logs = filterHotAnalyticsLogs(this.logs);
      } catch (err) {
        console.warn('[analytics] archive failed', err);
      }
    },

    setUserId(userId) {
      this.userId = userId != null && userId !== '' ? String(userId) : '';
      this.schedulePersist();
    },

    setPageVisible(visible) {
      const next = Boolean(visible);
      if (next === this.pageVisible) return;
      if (!next) {
        this._accrueCurrentPageDwell();
        this._accruePageEngage();
        void this.persistNow();
      } else {
        if (this.routeName) {
          this.routeEnteredAt = Date.now();
        }
        if (this.pageVisit?.readyAt && this.pageVisit.routeName === this.routeName) {
          this.pageVisit.engageStartedAt = Date.now();
        }
      }
      this.pageVisible = next;
    },

    _accrueCurrentPageDwell() {
      if (!this.routeName || !this.routeEnteredAt || !this.pageVisible) return;
      const delta = Date.now() - this.routeEnteredAt;
      if (delta <= 0) return;
      const prev = Number(this.pageDwellMs[this.routeName]) || 0;
      this.pageDwellMs[this.routeName] = prev + delta;
      this.routeEnteredAt = Date.now();
    },

    _accruePageEngage() {
      const visit = this.pageVisit;
      if (!visit?.readyAt || !visit.engageStartedAt || !this.pageVisible) return;
      if (visit.routeName !== this.routeName) return;
      const delta = Date.now() - visit.engageStartedAt;
      if (delta <= 0) return;
      const prev = Number(this.pageEngageMs[visit.routeName]) || 0;
      this.pageEngageMs[visit.routeName] = prev + delta;
      visit.engageStartedAt = Date.now();
    },

    enterRoute(routeName) {
      const name = String(routeName ?? '').trim() || '(unknown)';
      this._settlePageVisit('route_leave');
      this._accrueCurrentPageDwell();
      this.routeName = name;
      this.routeEnteredAt = Date.now();
      this._beginPageVisit(name);
      this.trackLog({
        key: 'page.enter',
        label: '进入页面',
        value: { routeName: name },
      });
      this.schedulePersist();
    },

    _beginPageVisit(routeName) {
      this.pageVisit = {
        id: createDeviceId(),
        routeName,
        enteredAt: Date.now(),
        readyAt: null,
        engageStartedAt: null,
        cold: null,
        apiMs: 0,
        apiCount: 0,
        jsMs: 0,
        dataMs: 0,
        apis: [],
      };
      this.trackLog({
        key: 'page.load.start',
        label: '页面加载开始',
        value: { routeName, visitId: this.pageVisit.id },
      });
    },

    /**
     * @param {'route_leave'|'hide'|'manual'} reason
     */
    _settlePageVisit(reason) {
      const visit = this.pageVisit;
      if (!visit) return;
      const engageBase = visit._engageBaseMs != null ? visit._engageBaseMs : 0;
      this._accruePageEngage();
      if (visit.readyAt) {
        const engageThisVisit = Math.max(
          0,
          (Number(this.pageEngageMs[visit.routeName]) || 0) - engageBase
        );
        this.trackLog({
          key: 'page.engage.end',
          label: '页面真留存结束',
          value: {
            routeName: visit.routeName,
            visitId: visit.id,
            reason,
            cold: visit.cold,
            loadMs: Math.max(0, visit.readyAt - visit.enteredAt),
            apiMs: visit.apiMs,
            dataMs: visit.dataMs,
            jsMs: visit.jsMs,
            engageTotalMs: Number(this.pageEngageMs[visit.routeName]) || 0,
          },
          durationMs: engageThisVisit,
        });
      } else {
        this.trackLog({
          key: 'page.load.abandon',
          label: '离开时尚未就绪',
          value: {
            routeName: visit.routeName,
            visitId: visit.id,
            reason,
            cold: visit.cold,
            apiMs: visit.apiMs,
            dataMs: visit.dataMs,
            jsMs: visit.jsMs,
            apis: visit.apis.slice(),
          },
          durationMs: Math.max(0, Date.now() - visit.enteredAt),
        });
      }
      this.pageVisit = null;
    },

    /**
     * Home 等页在拉数前标记冷/热启动。
     * @param {boolean} cold
     */
    setPageLoadCold(cold) {
      if (!this.pageVisit || this.pageVisit.readyAt) return;
      this.pageVisit.cold = Boolean(cold);
    },

    /**
     * 加载阶段接口耗时（ready 前）。
     * @param {{ key: string, durationMs: number, ok: boolean }} info
     */
    notePageLoadApi(info) {
      const visit = this.pageVisit;
      if (!visit || visit.readyAt) return;
      const ms = Math.max(0, Math.round(Number(info?.durationMs) || 0));
      visit.apiMs += ms;
      visit.apiCount += 1;
      if (visit.apis.length < 12) {
        visit.apis.push({
          key: String(info?.key || ''),
          ms,
          ok: Boolean(info?.ok),
        });
      }
    },

    /**
     * 加载阶段前端同步耗时（映射/重排等）。
     * @param {number} durationMs
     * @param {string} [phase]
     */
    notePageLoadJs(durationMs, phase = 'js') {
      const visit = this.pageVisit;
      if (!visit || visit.readyAt) return;
      const ms = Math.max(0, Math.round(Number(durationMs) || 0));
      visit.jsMs += ms;
      this.trackLog({
        key: 'page.load.phase',
        label: '页面加载-前端',
        value: {
          routeName: visit.routeName,
          visitId: visit.id,
          phase,
          kind: 'js',
        },
        durationMs: ms,
      });
    },

    /**
     * 加载阶段业务拉数墙钟（含 await 接口 + store 处理）。
     * @param {number} durationMs
     */
    notePageLoadData(durationMs) {
      const visit = this.pageVisit;
      if (!visit || visit.readyAt) return;
      const ms = Math.max(0, Math.round(Number(durationMs) || 0));
      visit.dataMs += ms;
      this.trackLog({
        key: 'page.load.phase',
        label: '页面加载-数据',
        value: {
          routeName: visit.routeName,
          visitId: visit.id,
          phase: 'data',
          kind: 'data',
          apiMs: visit.apiMs,
          apiCount: visit.apiCount,
        },
        durationMs: ms,
      });
    },

    /**
     * 首屏就绪：之后累计真留存。
     * @param {{ result?: string, rowCount?: number }} [info]
     */
    markPageReady(info = {}) {
      const visit = this.pageVisit;
      if (!visit || visit.readyAt) return;
      if (visit.routeName !== this.routeName) return;
      const now = Date.now();
      visit.readyAt = now;
      visit._engageBaseMs = Number(this.pageEngageMs[visit.routeName]) || 0;
      const loadMs = Math.max(0, now - visit.enteredAt);
      const residualMs = Math.max(0, loadMs - visit.apiMs - visit.jsMs);
      this.trackLog({
        key: 'page.ready',
        label: '页面可交互',
        value: {
          routeName: visit.routeName,
          visitId: visit.id,
          cold: visit.cold,
          result: info.result || 'unknown',
          rowCount: info.rowCount,
          loadMs,
          apiMs: visit.apiMs,
          apiCount: visit.apiCount,
          dataMs: visit.dataMs,
          jsMs: visit.jsMs,
          residualMs,
          apis: visit.apis.slice(),
        },
        durationMs: loadMs,
      });
      if (this.pageVisible) {
        visit.engageStartedAt = now;
      }
      this.schedulePersist();
    },

    /**
     * @param {string} flowKey
     */
    beginFlow(flowKey) {
      const key = String(flowKey ?? '').trim();
      if (!key) return;
      if (!this.flowStartedAt[key]) {
        this.flowStartedAt[key] = Date.now();
      }
    },

    /**
     * @param {string} flowKey
     * @returns {number|undefined}
     */
    endFlow(flowKey) {
      const key = String(flowKey ?? '').trim();
      if (!key) return undefined;
      const started = this.flowStartedAt[key];
      if (started == null) return undefined;
      delete this.flowStartedAt[key];
      return Math.max(0, Date.now() - started);
    },

    /**
     * @param {string} flowKey
     * @returns {number|undefined}
     */
    peekFlowMs(flowKey) {
      const key = String(flowKey ?? '').trim();
      const started = this.flowStartedAt[key];
      if (started == null) return undefined;
      return Math.max(0, Date.now() - started);
    },

    /**
     * 确保漏斗会话存在；传 flowKey 时按业务域隔离。
     * @param {string} [flowKey]
     * @returns {string}
     */
    ensureFunnelSession(flowKey) {
      const key = String(flowKey ?? '').trim();
      if (key) {
        const existing = this.funnelSessions[key];
        if (existing?.id) return existing.id;
        const session = { id: createDeviceId(), startedAt: Date.now() };
        this.funnelSessions = { ...this.funnelSessions, [key]: session };
        void this.persistNow();
        return session.id;
      }
      if (!this.funnelSessionId) {
        this.funnelSessionId = createDeviceId();
        this.funnelStartedAt = Date.now();
        void this.persistNow();
      }
      return this.funnelSessionId;
    },

    /**
     * 恢复跨页漏斗会话（如 H5 onramp 回跳）。
     * @param {string} sessionId
     * @param {string} [flowKey]
     * @returns {string}
     */
    restoreFunnelSession(sessionId, flowKey) {
      const id = String(sessionId ?? '').trim();
      const key = String(flowKey ?? '').trim();
      if (key) {
        if (!id) return this.ensureFunnelSession(key);
        if (this.funnelSessions[key]?.id !== id) {
          this.funnelSessions = {
            ...this.funnelSessions,
            [key]: {
              id,
              startedAt: Date.now(),
            },
          };
          void this.persistNow();
        }
        return this.funnelSessions[key]?.id || id;
      }
      if (!id) return this.ensureFunnelSession();
      if (this.funnelSessionId !== id) {
        this.funnelSessionId = id;
        this.funnelStartedAt = this.funnelStartedAt || Date.now();
        void this.persistNow();
      }
      return this.funnelSessionId;
    },

    /** @param {string} [flowKey] @returns {string} 结束前的 session id */
    endFunnelSession(flowKey) {
      const key = String(flowKey ?? '').trim();
      if (key) {
        const id = this.funnelSessions[key]?.id || '';
        if (this.funnelSessions[key]) {
          const next = { ...this.funnelSessions };
          delete next[key];
          this.funnelSessions = next;
          void this.persistNow();
        }
        return id;
      }
      const id = this.funnelSessionId || '';
      this.funnelSessionId = '';
      this.funnelStartedAt = 0;
      void this.persistNow();
      return id;
    },

    /**
     * @param {{
     *   key: string,
     *   label?: string,
     *   value?: unknown,
     *   props?: unknown,
     *   funnelSessionId?: string,
     *   durationMs?: number,
     * }} entry
     */
    trackLog(entry) {
      if (!this.hydrated) {
        pendingTrackEntries.push(entry);
        void this.hydrate();
        return;
      }
      const row = buildAnalyticsLogRow(entry, {
        deviceId: this.deviceId || undefined,
        userId: this.userId || undefined,
        routeName: this.routeName || undefined,
      });
      if (!row) return;
      this.logs.push(row);
      this.logs = filterHotAnalyticsLogs(this.logs);
      this.schedulePersist();
      trackLaEventByKey(row.key, { dedupeKey: resolveLaOutboxDedupeKey(row) });
      // 动态 import：避免与 analytics-track / visit-engaged 循环依赖
      void import('@/utils/analytics-visit-engaged.js')
        .then((m) => {
          m.maybeTrackVisitEngagedAction?.(
            row.key,
            row.props && typeof row.props === 'object' ? row.props : null
          );
        })
        .catch(() => {});
    },

    /**
     * @deprecated 使用 trackLog；保留兼容
     * @param {string} name
     * @param {Record<string, unknown>} [extra]
     */
    trackAction(name, extra) {
      this.trackLog({
        key: String(name ?? '').trim(),
        label: String(name ?? '').trim(),
        props: extra ?? null,
      });
    },

    recordApi(info) {
      if (!this.hydrated) {
        void this.hydrate();
        return;
      }
      const key = normalizeAnalyticsApiKey(info?.method, info?.url);
      if (!key) return;
      const durationMs = Math.max(0, Math.round(Number(info?.durationMs) || 0));
      const ok = Boolean(info?.ok);
      const prev = this.apiActivity[key];
      const method = String(info?.method || 'GET')
        .trim()
        .toUpperCase() || 'GET';
      this.apiActivity[key] = {
        key,
        method: prev?.method || method,
        hits: (prev?.hits || 0) + 1,
        lastAt: Date.now(),
        lastOk: ok,
        lastDurationMs: durationMs,
        lastCode: info?.code ?? null,
      };
      this.notePageLoadApi({ key, durationMs, ok });
      this.schedulePersist();
    },
  },
});

export { ANALYTICS_LOG_SCHEMA_VERSION };
